import { Brand } from "./brands";

/**
 * Each category defines internal slug, bilingual URL slugs, labels, SEO metadata, and a filter function.
 */
export interface Top10Category {
  /** Internal identifier – used as key for icons, intros, etc. */
  slug: string;
  /** URL slug used on French pages */
  slugFr: string;
  /** URL slug used on English pages */
  slugEn: string;
  labelFr: string;
  labelEn: string;
  /** H1 / meta title for French page */
  metaTitleFr: string;
  /** H1 / meta title for English page */
  metaTitleEn: string;
  metaDescFr: string;
  metaDescEn: string;
  /** Dynamic meta description builder – receives top brand names */
  metaDescFrDynamic?: (brand1: string, brand2: string) => string;
  metaDescEnDynamic?: (brand1: string, brand2: string) => string;
  /** Custom filter applied to brands array */
  filter: (brand: Brand) => boolean;
}

const includes = (arr: string[] | undefined, value: string) =>
  arr?.some((v) => v.toLowerCase() === value.toLowerCase()) ?? false;

const includesAny = (arr: string[] | undefined, values: string[]) =>
  values.some((v) => includes(arr, v));

export const top10Categories: Top10Category[] = [
  {
    slug: "baskets",
    slugFr: "baskets-eco-responsables",
    slugEn: "sustainable-sneakers",
    labelFr: "Baskets Éco-responsables",
    labelEn: "Sustainable Sneakers",
    metaTitleFr: "Top 10 Baskets Éco-responsables [Crash-test 2026] 🧭",
    metaTitleEn: "10 Best Sustainable Sneakers [Crash-test 2026] 🧭",
    metaDescFr: "Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 meilleures marques de baskets durables.",
    metaDescEn: "Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the 10 best sustainable sneaker brands.",
    metaDescFrDynamic: (b1, b2) => `Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques de baskets comme ${b1} et ${b2} qui ne trichent pas.`,
    metaDescEnDynamic: (b1, b2) => `Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 sneaker brands like ${b1} and ${b2} that don't cheat.`,
    filter: (b) =>
      b.productSegment?.toLowerCase() === "footwear" &&
      includes(b.productTypes, "Sneakers"),
  },
  {
    slug: "t-shirts-coton-bio",
    slugFr: "t-shirts-coton-bio",
    slugEn: "organic-cotton-t-shirts",
    labelFr: "T-shirts Coton Bio",
    labelEn: "Organic Cotton T-shirts",
    metaTitleFr: "Top 10 T-shirts en Coton Bio [Crash-test 2026] 🧭",
    metaTitleEn: "10 Best Organic Cotton T-shirts [Crash-test 2026] 🧭",
    metaDescFr: "Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 t-shirts en coton bio les plus durables.",
    metaDescEn: "Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the 10 most durable organic cotton t-shirts.",
    metaDescFrDynamic: (b1, b2) => `Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques de t-shirts bio comme ${b1} et ${b2} qui ne trichent pas.`,
    metaDescEnDynamic: (b1, b2) => `Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 organic cotton t-shirt brands like ${b1} and ${b2} that don't cheat.`,
    filter: (b) =>
      b.productSegment?.toLowerCase() === "apparel" &&
      includes(b.productTypes, "T-shirts") &&
      (b.subScoreElements?.materialImpact || "").toLowerCase().includes("organic cotton"),
  },
  {
    slug: "sous-vetements",
    slugFr: "sous-vetements-ethiques",
    slugEn: "ethical-underwear",
    labelFr: "Sous-vêtements Éthiques",
    labelEn: "Ethical Underwear",
    metaTitleFr: "Meilleures Marques Sous-vêtements Éthiques [Crash-test 2026] 🧭",
    metaTitleEn: "10 Best Ethical Underwear Brands [2026 Crash-test] 🧭",
    metaDescFr: "Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques qui ne trichent pas.",
    metaDescEn: "Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 brands that don't cheat.",
    metaDescFrDynamic: (b1, b2) => `Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques comme ${b1} et ${b2} qui ne trichent pas.`,
    metaDescEnDynamic: (b1, b2) => `Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 brands like ${b1} and ${b2} that don't cheat.`,
    filter: (b) => includesAny(b.productTypes, ["Underwear", "Lingerie"]),
  },
  {
    slug: "maillots-de-bain",
    slugFr: "maillots-de-bain-eco-responsables",
    slugEn: "sustainable-swimwear",
    labelFr: "Maillots de Bain Éco-responsables",
    labelEn: "Sustainable Swimwear",
    metaTitleFr: "Top 10 Maillots de Bain Éco-responsables [Crash-test 2026] 🧭",
    metaTitleEn: "10 Best Sustainable Swimwear Brands [Crash-test 2026] 🧭",
    metaDescFr: "Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 meilleures marques de maillots de bain durables.",
    metaDescEn: "Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the 10 best sustainable swimwear brands.",
    metaDescFrDynamic: (b1, b2) => `Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques de maillots comme ${b1} et ${b2} qui ne trichent pas.`,
    metaDescEnDynamic: (b1, b2) => `Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 swimwear brands like ${b1} and ${b2} that don't cheat.`,
    filter: (b) => b.productSegment?.toLowerCase() === "intimates & swim",
  },
  {
    slug: "chaussures-vegan",
    slugFr: "chaussures-vegan-ethiques",
    slugEn: "vegan-footwear",
    labelFr: "Chaussures Vegan & Éthiques",
    labelEn: "Vegan & Ethical Footwear",
    metaTitleFr: "Top 10 Chaussures Vegan & Éthiques [Crash-test 2026] 🧭",
    metaTitleEn: "10 Best 100% Vegan & Ethical Footwear [Crash-test 2026] 🧭",
    metaDescFr: "Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 chaussures 100% vegan et durables.",
    metaDescEn: "Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 100% vegan and sustainable shoes.",
    metaDescFrDynamic: (b1, b2) => `Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques de chaussures vegan comme ${b1} et ${b2} qui ne trichent pas.`,
    metaDescEnDynamic: (b1, b2) => `Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 vegan footwear brands like ${b1} and ${b2} that don't cheat.`,
    filter: (b) =>
      b.productSegment?.toLowerCase() === "footwear" &&
      b.veganBrand?.toLowerCase() === "100% vegan",
  },
  {
    slug: "outdoor-equipement",
    slugFr: "equipement-outdoor-ethique",
    slugEn: "sustainable-outdoor-gear",
    labelFr: "Équipement Outdoor Éthique",
    labelEn: "Sustainable Outdoor Gear",
    metaTitleFr: "Outdoor & Gear : Top 10 Marques Éthiques [Crash-test 2026] 🧭",
    metaTitleEn: "10 Best Sustainable Outdoor Gear Brands [Crash-test 2026] 🧭",
    metaDescFr: "Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 meilleures marques d'équipement outdoor durables.",
    metaDescEn: "Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the 10 best sustainable outdoor gear brands.",
    metaDescFrDynamic: (b1, b2) => `Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques outdoor comme ${b1} et ${b2} qui ne trichent pas.`,
    metaDescEnDynamic: (b1, b2) => `Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 outdoor gear brands like ${b1} and ${b2} that don't cheat.`,
    filter: (b) =>
      b.productSegment?.toLowerCase() === "outdoor & gear",
  },
  {
    slug: "vetements-homme",
    slugFr: "mode-homme-ethique",
    slugEn: "sustainable-mens-clothing",
    labelFr: "Mode Homme Éthique",
    labelEn: "Sustainable Men's Clothing",
    metaTitleFr: "Les 10 Meilleures Marques Mode Homme Éthique [Crash-test 2026] 🧭",
    metaTitleEn: "10 Best Sustainable Men's Clothing Brands [Crash-test 2026] 🧭",
    metaDescFr: "Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 meilleures marques de mode homme durables.",
    metaDescEn: "Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the 10 best sustainable men's clothing brands.",
    metaDescFrDynamic: (b1, b2) => `Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques homme comme ${b1} et ${b2} qui ne trichent pas.`,
    metaDescEnDynamic: (b1, b2) => `Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 men's brands like ${b1} and ${b2} that don't cheat.`,
    filter: (b) =>
      b.productSegment?.toLowerCase() === "apparel" &&
      includesAny(b.targetCustomer, ["Men"]),
  },
  {
    slug: "vetements-bebe-enfant",
    slugFr: "vetements-bebe-ethique",
    slugEn: "sustainable-baby-kids-clothing",
    labelFr: "Vêtements Bébé & Enfant Éthiques",
    labelEn: "Sustainable Baby & Kids Clothing",
    metaTitleFr: "Top 10 Vêtements Bébé & Enfant Éthiques [Crash-test 2026] 🧭",
    metaTitleEn: "10 Best Eco-Friendly Baby & Kids Clothing [Crash-test 2026] 🧭",
    metaDescFr: "Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 meilleures marques de vêtements bébé durables.",
    metaDescEn: "Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the 10 best eco-friendly baby & kids clothing brands.",
    metaDescFrDynamic: (b1, b2) => `Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques bébé comme ${b1} et ${b2} qui ne trichent pas.`,
    metaDescEnDynamic: (b1, b2) => `Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 baby & kids brands like ${b1} and ${b2} that don't cheat.`,
    filter: (b) =>
      b.productSegment?.toLowerCase() === "apparel" &&
      includesAny(b.targetCustomer, ["Kids", "Baby"]),
  },
  {
    slug: "vetements-vegan",
    slugFr: "mode-vegan-ethique",
    slugEn: "100-percent-vegan-clothing",
    labelFr: "Mode 100% Vegan & Éthique",
    labelEn: "100% Vegan & Ethical Fashion",
    metaTitleFr: "Top 10 Mode 100% Vegan et Éthique [Crash-test 2026] 🧭",
    metaTitleEn: "10 Best 100% Vegan & Ethical Fashion Brands [Crash-test 2026] 🧭",
    metaDescFr: "Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 meilleures marques vegan qui respectent les animaux.",
    metaDescEn: "Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the 10 best vegan brands that respect animals.",
    metaDescFrDynamic: (b1, b2) => `Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques vegan comme ${b1} et ${b2} qui ne trichent pas.`,
    metaDescEnDynamic: (b1, b2) => `Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 vegan brands like ${b1} and ${b2} that don't cheat.`,
    filter: (b) =>
      b.productSegment?.toLowerCase() === "apparel" &&
      b.veganBrand?.toLowerCase() === "100% vegan",
  },
  {
    slug: "abordable",
    slugFr: "mode-ethique-pas-chere",
    slugEn: "affordable-ethical-fashion",
    labelFr: "Mode Éthique Abordable",
    labelEn: "Affordable Ethical Fashion",
    metaTitleFr: "Mode Éthique Pas Chère : Top 10 Marques [Crash-test 2026] 🧭",
    metaTitleEn: "10 Best Affordable Ethical Fashion Brands [Crash-test 2026] 🧭",
    metaDescFr: "Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques de mode durable à prix doux.",
    metaDescEn: "Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the 10 best affordable ethical fashion brands.",
    metaDescFrDynamic: (b1, b2) => `Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques abordables comme ${b1} et ${b2} qui ne trichent pas.`,
    metaDescEnDynamic: (b1, b2) => `Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 affordable brands like ${b1} and ${b2} that don't cheat.`,
    filter: (b) =>
      b.productSegment?.toLowerCase() === "apparel" && b.priceCategory === "low-budget",
  },
  {
    slug: "jeans",
    slugFr: "top-10-jeans-ethiques-durables",
    slugEn: "top-10-best-ethical-jeans-brands",
    labelFr: "Jeans Éthiques",
    labelEn: "Ethical Jeans",
    metaTitleFr: "Top 10 des Meilleurs Jeans Éthiques [Crash-test 2026] 🧭",
    metaTitleEn: "Top 10 Best Ethical Jeans Brands [Crash-test 2026] 🧭",
    metaDescFr: "Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 meilleures marques de jeans durables.",
    metaDescEn: "Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 ethical jeans brands.",
    metaDescFrDynamic: (b1, b2) => `Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques de jeans comme ${b1} et ${b2} qui ne trichent pas.`,
    metaDescEnDynamic: (b1, b2) => `Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 jeans brands like ${b1} and ${b2} that don't cheat.`,
    filter: (b) => includes(b.productTypes, "Jeans"),
  },
  {
    slug: "plus-size",
    slugFr: "marques-mode-ethique-plus-size",
    slugEn: "ethical-plus-size-fashion-brands",
    labelFr: "Mode Plus Size Éthique",
    labelEn: "Ethical Plus Size Fashion",
    metaTitleFr: "Top 10 Marques Plus Size Éthiques [Crash-test 2026] 🧭",
    metaTitleEn: "Top 10 Ethical Plus Size Brands [Crash-test 2026] 🧭",
    metaDescFr: "Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques Plus Size qui ne trichent pas.",
    metaDescEn: "Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 Plus Size brands that don't cheat.",
    metaDescFrDynamic: (b1, b2) => `Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques Plus Size comme ${b1} et ${b2} qui ne trichent pas.`,
    metaDescEnDynamic: (b1, b2) => `Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 Plus Size brands like ${b1} and ${b2} that don't cheat.`,
    filter: (b) => b.plusSize?.toLowerCase() === "yes",
  },
  {
    slug: "made-in-france",
    slugFr: "marques-made-in-france-ethique",
    slugEn: "ethical-made-in-france-brands",
    labelFr: "Made in France Éthique",
    labelEn: "Ethical Made in France",
    metaTitleFr: "Top 10 Marques Made in France Éthiques [Crash-test 2026] 🧭",
    metaTitleEn: "10 Best Made in France Ethical Brands [Crash-test 2026] 🧭",
    metaDescFr: "Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 meilleures marques Made in France.",
    metaDescEn: "Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the 10 best Made in France ethical brands.",
    metaDescFrDynamic: (b1, b2) => `Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques Made in France comme ${b1} et ${b2} qui ne trichent pas.`,
    metaDescEnDynamic: (b1, b2) => `Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 Made in France brands like ${b1} and ${b2} that don't cheat.`,
    filter: (b) =>
      Array.isArray(b.productionCountries) &&
      b.productionCountries.length === 1 &&
      b.productionCountries[0].toLowerCase() === "france",
  },
  {
    slug: "trail-running",
    slugFr: "top-10-marques-trail-running-ethique",
    slugEn: "top-10-best-ethical-trail-running-brands",
    labelFr: "Trail Running Éthique",
    labelEn: "Ethical Trail Running",
    metaTitleFr: "Top 10 Marques de Trail Éthiques [Crash-test 2026] 🧭",
    metaTitleEn: "10 Best Ethical Trail Running Brands [Crash-test 2026] 🧭",
    metaDescFr: "Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 meilleures marques de trail durables.",
    metaDescEn: "Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the 10 best ethical trail running brands.",
    metaDescFrDynamic: (b1, b2) => `Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques de trail comme ${b1} et ${b2} qui ne trichent pas.`,
    metaDescEnDynamic: (b1, b2) => `Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 trail running brands like ${b1} and ${b2} that don't cheat.`,
    filter: (b) => includes(b.tags, "Trail"),
  },
  {
    slug: "yoga",
    slugFr: "top-10-marques-yoga-ethique",
    slugEn: "top-10-best-ethical-yoga-brands",
    labelFr: "Yoga Éthique",
    labelEn: "Ethical Yoga",
    metaTitleFr: "Top 10 Marques de Yoga Éthiques [Crash-test 2026] 🧭",
    metaTitleEn: "10 Best Ethical Yoga Brands [Crash-test 2026] 🧭",
    metaDescFr: "Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 meilleures marques de yoga durables.",
    metaDescEn: "Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the 10 best ethical yoga brands.",
    metaDescFrDynamic: (b1, b2) => `Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques de yoga comme ${b1} et ${b2} qui ne trichent pas.`,
    metaDescEnDynamic: (b1, b2) => `Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 yoga brands like ${b1} and ${b2} that don't cheat.`,
    filter: (b) => includes(b.tags, "Yoga"),
  },
  {
    slug: "hiking",
    slugFr: "top-10-marques-randonnee-durables",
    slugEn: "top-10-best-sustainable-hiking-brands",
    labelFr: "Randonnée Durable",
    labelEn: "Sustainable Hiking",
    metaTitleFr: "Top 10 Marques de Randonnée Durables [Crash-test 2026] 🧭",
    metaTitleEn: "10 Best Sustainable Hiking Brands [Crash-test 2026] 🧭",
    metaDescFr: "Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 meilleures marques de randonnée durables.",
    metaDescEn: "Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the 10 best sustainable hiking brands.",
    metaDescFrDynamic: (b1, b2) => `Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques de randonnée comme ${b1} et ${b2} qui ne trichent pas.`,
    metaDescEnDynamic: (b1, b2) => `Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 hiking brands like ${b1} and ${b2} that don't cheat.`,
    filter: (b) => includes(b.tags, "Hiking"),
  },
  {
    slug: "surf-beachwear",
    slugFr: "top-10-marques-surf-beachwear-ethique",
    slugEn: "top-10-ethical-surf-beachwear-brands",
    labelFr: "Surf & Beachwear Éthique",
    labelEn: "Ethical Surf & Beachwear",
    metaTitleFr: "Top 10 Marques de Surf et Beachwear [Crash-test 2026] 🧭",
    metaTitleEn: "10 Best Ethical Surf & Beachwear Brands [Crash-test 2026] 🧭",
    metaDescFr: "Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 meilleures marques de surf durables.",
    metaDescEn: "Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the 10 best ethical surf & beachwear brands.",
    metaDescFrDynamic: (b1, b2) => `Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques de surf comme ${b1} et ${b2} qui ne trichent pas.`,
    metaDescEnDynamic: (b1, b2) => `Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 surf & beachwear brands like ${b1} and ${b2} that don't cheat.`,
    filter: (b) => includes(b.tags, "Surf & beachwear"),
  },
  {
    slug: "workwear",
    slugFr: "top-10-marques-workwear-durables",
    slugEn: "top-10-best-durable-workwear-brands",
    labelFr: "Workwear Durable",
    labelEn: "Durable Workwear",
    metaTitleFr: "Top 10 Marques de Workwear Durables [Crash-test 2026] 🧭",
    metaTitleEn: "10 Best Durable Workwear Brands [Crash-test 2026] 🧭",
    metaDescFr: "Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques de workwear les plus durables.",
    metaDescEn: "Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 most durable workwear brands.",
    metaDescFrDynamic: (b1, b2) => `Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques de workwear comme ${b1} et ${b2} qui ne trichent pas.`,
    metaDescEnDynamic: (b1, b2) => `Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 workwear brands like ${b1} and ${b2} that don't cheat.`,
    filter: (b) => includes(b.tags, "Workwear"),
  },
  {
    slug: "office-wear",
    slugFr: "top-10-marques-office-wear-ethique",
    slugEn: "top-10-best-ethical-office-wear-brands",
    labelFr: "Office Wear Éthique",
    labelEn: "Ethical Office Wear",
    metaTitleFr: "Top 10 Marques de Bureau Éthiques [Crash-test 2026] 🧭",
    metaTitleEn: "10 Best Ethical Office Wear Brands [Crash-test 2026] 🧭",
    metaDescFr: "Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 meilleures marques Office Wear durables.",
    metaDescEn: "Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the 10 best ethical office wear brands.",
    metaDescFrDynamic: (b1, b2) => `Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques Office Wear comme ${b1} et ${b2} qui ne trichent pas.`,
    metaDescEnDynamic: (b1, b2) => `Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 office wear brands like ${b1} and ${b2} that don't cheat.`,
    filter: (b) => includes(b.tags, "Office_wear"),
  },
  {
    slug: "freeride",
    slugFr: "top-10-marques-freeride-ski-ethique",
    slugEn: "top-10-ethical-freeride-ski-brands",
    labelFr: "Freeride & Ski Éthique",
    labelEn: "Ethical Freeride & Ski",
    metaTitleFr: "Top 10 Marques de Freeride et Ski [Crash-test 2026] 🧭",
    metaTitleEn: "10 Best Ethical Freeride & Ski Brands [Crash-test 2026] 🧭",
    metaDescFr: "Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques de ski et freeride les plus durables.",
    metaDescEn: "Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 ethical freeride & ski brands.",
    metaDescFrDynamic: (b1, b2) => `Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques de freeride comme ${b1} et ${b2} qui ne trichent pas.`,
    metaDescEnDynamic: (b1, b2) => `Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 freeride brands like ${b1} and ${b2} that don't cheat.`,
    filter: (b) => includes(b.tags, "Freeride"),
  },
  {
    slug: "vetements-lin",
    slugFr: "top-10-vetements-lin-ethique",
    slugEn: "top-10-best-ethical-linen-brands",
    labelFr: "Vêtements en Lin Éthiques",
    labelEn: "Ethical Linen Clothing",
    metaTitleFr: "Top 10 Marques de Vêtements en Lin [Crash-test 2026] 🧭",
    metaTitleEn: "10 Best Ethical Linen Brands [Crash-test 2026] 🧭",
    metaDescFr: "Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 meilleures marques de lin durables.",
    metaDescEn: "Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the 10 best ethical linen brands.",
    metaDescFrDynamic: (b1, b2) => `Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques de lin comme ${b1} et ${b2} qui ne trichent pas.`,
    metaDescEnDynamic: (b1, b2) => `Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 linen brands like ${b1} and ${b2} that don't cheat.`,
    filter: (b) =>
      (b.subScoreElements?.materialImpact || "").toLowerCase().includes("linen (low impact fiber)"),
  },
  {
    slug: "made-in-usa",
    slugFr: "marques-made-in-usa-ethique",
    slugEn: "ethical-made-in-usa-brands",
    labelFr: "Made in USA Éthique",
    labelEn: "Ethical Made in USA",
    metaTitleFr: "Top 10 Marques Made in USA Éthiques [Crash-test 2026] 🧭",
    metaTitleEn: "10 Best Made in USA Ethical Brands [Crash-test 2026] 🧭",
    metaDescFr: "Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 meilleures marques Made in USA.",
    metaDescEn: "Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the 10 best Made in USA ethical brands.",
    metaDescFrDynamic: (b1, b2) => `Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques Made in USA comme ${b1} et ${b2} qui ne trichent pas.`,
    metaDescEnDynamic: (b1, b2) => `Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 Made in USA brands like ${b1} and ${b2} that don't cheat.`,
    filter: (b) =>
      Array.isArray(b.productionCountries) &&
      b.productionCountries.length === 1 &&
      b.productionCountries[0].toLowerCase() === "united states",
  },
  {
    slug: "made-in-portugal",
    slugFr: "marques-made-in-portugal-ethique",
    slugEn: "ethical-made-in-portugal-brands",
    labelFr: "Made in Portugal Éthique",
    labelEn: "Ethical Made in Portugal",
    metaTitleFr: "Top 10 Marques Made in Portugal Éthiques [Crash-test 2026] 🧭",
    metaTitleEn: "10 Best Made in Portugal Ethical Brands [Crash-test 2026] 🧭",
    metaDescFr: "Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 meilleures marques Made in Portugal.",
    metaDescEn: "Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the 10 best Made in Portugal ethical brands.",
    metaDescFrDynamic: (b1, b2) => `Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques Made in Portugal comme ${b1} et ${b2} qui ne trichent pas.`,
    metaDescEnDynamic: (b1, b2) => `Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 Made in Portugal brands like ${b1} and ${b2} that don't cheat.`,
    filter: (b) =>
      Array.isArray(b.productionCountries) &&
      b.productionCountries.length === 1 &&
      b.productionCountries[0].toLowerCase() === "portugal",
  },
  {
    slug: "made-in-spain",
    slugFr: "marques-made-in-spain-ethique",
    slugEn: "ethical-made-in-spain-brands",
    labelFr: "Made in Spain Éthique",
    labelEn: "Ethical Made in Spain",
    metaTitleFr: "Top 10 Marques Made in Spain Éthiques [Crash-test 2026] 🧭",
    metaTitleEn: "10 Best Made in Spain Ethical Brands [Crash-test 2026] 🧭",
    metaDescFr: "Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 meilleures marques Made in Spain.",
    metaDescEn: "Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the 10 best Made in Spain ethical brands.",
    metaDescFrDynamic: (b1, b2) => `Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques Made in Spain comme ${b1} et ${b2} qui ne trichent pas.`,
    metaDescEnDynamic: (b1, b2) => `Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 Made in Spain brands like ${b1} and ${b2} that don't cheat.`,
    filter: (b) =>
      Array.isArray(b.productionCountries) &&
      b.productionCountries.length === 1 &&
      b.productionCountries[0].toLowerCase() === "spain",
  },
  {
    slug: "made-in-canada",
    slugFr: "marques-made-in-canada-ethique",
    slugEn: "ethical-made-in-canada-brands",
    labelFr: "Made in Canada Éthique",
    labelEn: "Ethical Made in Canada",
    metaTitleFr: "Top 10 Marques Made in Canada Éthiques [Crash-test 2026] 🧭",
    metaTitleEn: "10 Best Made in Canada Ethical Brands [Crash-test 2026] 🧭",
    metaDescFr: "Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 meilleures marques Made in Canada.",
    metaDescEn: "Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the 10 best Made in Canada ethical brands.",
    metaDescFrDynamic: (b1, b2) => `Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques Made in Canada comme ${b1} et ${b2} qui ne trichent pas.`,
    metaDescEnDynamic: (b1, b2) => `Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 Made in Canada brands like ${b1} and ${b2} that don't cheat.`,
    filter: (b) =>
      Array.isArray(b.productionCountries) &&
      b.productionCountries.length === 1 &&
      b.productionCountries[0].toLowerCase() === "canada",
  },
  {
    slug: "made-in-australia",
    slugFr: "marques-made-in-australia-ethique",
    slugEn: "ethical-made-in-australia-brands",
    labelFr: "Made in Australia Éthique",
    labelEn: "Ethical Made in Australia",
    metaTitleFr: "Top 10 Marques Made in Australia Éthiques [Crash-test 2026] 🧭",
    metaTitleEn: "10 Best Made in Australia Ethical Brands [Crash-test 2026] 🧭",
    metaDescFr: "Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 meilleures marques Made in Australia.",
    metaDescEn: "Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the 10 best Made in Australia ethical brands.",
    metaDescFrDynamic: (b1, b2) => `Matières, CO2, fiscalité et emplois locaux : on crash-test tout pour le VRAI éthique. Découvrez les 10 marques Made in Australia comme ${b1} et ${b2} qui ne trichent pas.`,
    metaDescEnDynamic: (b1, b2) => `Materials, CO2, tax & local jobs: we crash-test it all for TRUE ethics. Discover the top 10 Made in Australia brands like ${b1} and ${b2} that don't cheat.`,
    filter: (b) =>
      Array.isArray(b.productionCountries) &&
      b.productionCountries.length === 1 &&
      b.productionCountries[0].toLowerCase() === "australia",
  },
  {
    slug: "cycling",
    slugFr: "top-10-cyclisme-ethique-2026",
    slugEn: "top-10-ethical-cycling-brands-2026",
    labelFr: "Cyclisme Éthique",
    labelEn: "Ethical Cycling",
    metaTitleFr: "Top 10 Marques de Cyclisme Éthiques [Crash-test 2026] 🧭",
    metaTitleEn: "Top 10 Ethical Cycling Brands [2026 Crash-Test] 🧭",
    metaDescFr: "CO2, fiscalité et PFC : on décortique tout. Découvrez les 10 marques de vélo qui allient performance aéro et intégrité radicale. Ne pédalez plus à contre-courant de vos valeurs.",
    metaDescEn: "CO2, taxes, and PFAS: we audit everything. Discover the 10 cycling brands combining aero performance with radical integrity. Stop riding against your values.",
    metaDescFrDynamic: (b1, b2) => `CO2, fiscalité et PFC : on décortique tout. Découvrez les 10 marques de vélo comme ${b1} et ${b2} qui allient performance aéro et intégrité radicale.`,
    metaDescEnDynamic: (b1, b2) => `CO2, taxes, and PFAS: we audit everything. Discover the top 10 cycling brands like ${b1} and ${b2} combining aero performance with radical integrity.`,
    filter: (b) => includes(b.tags, "Cycling"),
  },
  {
    slug: "climbing",
    slugFr: "top-10-escalade-ethique-2026",
    slugEn: "top-10-ethical-climbing-brands-2026",
    labelFr: "Escalade Éthique",
    labelEn: "Ethical Climbing",
    metaTitleFr: "Top 10 Marques d'Escalade Éthiques [Crash-test 2026] 🧭",
    metaTitleEn: "Top 10 Ethical Climbing Brands [2026 Crash-Test] 🧭",
    metaDescFr: "Gommes, PFAS, cordes recyclées et fiscalité : on crash-test tout. Découvrez les 10 marques d'escalade qui allient grimpe extrême et éthique radicale.",
    metaDescEn: "Rubber, PFAS, recycled ropes, and taxes: we audit everything. Discover the 10 climbing brands combining vertical performance with radical integrity.",
    metaDescFrDynamic: (b1, b2) => `Gommes, PFAS, cordes recyclées et fiscalité : on crash-test tout. Découvrez les 10 marques d'escalade comme ${b1} et ${b2} qui allient grimpe extrême et éthique radicale.`,
    metaDescEnDynamic: (b1, b2) => `Rubber, PFAS, recycled ropes, and taxes: we audit everything. Discover the 10 climbing brands like ${b1} and ${b2} combining vertical performance with radical integrity.`,
    filter: (b) => includes(b.tags, "Climbing"),
  },
  {
    slug: "luxe",
    slugFr: "meilleures-marques-luxe-ethiques",
    slugEn: "best-ethical-luxury-brands",
    labelFr: "Luxe Éthique",
    labelEn: "Ethical Luxury",
    metaTitleFr: "Top 10 Marques de Luxe Éthiques [Crash-test 2026] 🧭",
    metaTitleEn: "10 Best Ethical Luxury Brands [Crash-test 2026] 🧭",
    metaDescFr: "Le prix n'est pas une preuve d'éthique. Nous avons crash-testé les plus grandes maisons de luxe : CO2, impôts, social. Découvrez celles qui ne triche pas.",
    metaDescEn: "Price tag isn't a guarantee of ethics. We crash-tested the world's top luxury houses on 15 criteria: CO2, tax transparency, and local jobs. Discover the true elite.",
    metaDescFrDynamic: (b1, b2) => `Le prix n'est pas une preuve d'éthique. Nous avons crash-testé les maisons de luxe comme ${b1} et ${b2} : CO2, impôts, social. Découvrez celles qui ne trichent pas.`,
    metaDescEnDynamic: (b1, b2) => `Price tag isn't a guarantee of ethics. We crash-tested luxury houses like ${b1} and ${b2} on 15 criteria: CO2, tax transparency, and local jobs.`,
    filter: (b) => b.priceCategory === "luxury",
  },
];

/** Map of old slugs to internal slug for redirects */
export const oldSlugMap: Record<string, string> = {
  baskets: "baskets",
  "t-shirts-coton-bio": "t-shirts-coton-bio",
  "sous-vetements": "sous-vetements",
  "maillots-de-bain": "maillots-de-bain",
  "chaussures-vegan": "chaussures-vegan",
  "outdoor-equipement": "outdoor-equipement",
  "vetements-homme": "vetements-homme",
  "vetements-bebe-enfant": "vetements-bebe-enfant",
  "vetements-vegan": "vetements-vegan",
  abordable: "abordable",
  "made-in-france": "made-in-france",
  jeans: "jeans",
  "plus-size": "plus-size",
  "trail-running": "trail-running",
  yoga: "yoga",
  hiking: "hiking",
  "surf-beachwear": "surf-beachwear",
  workwear: "workwear",
  "office-wear": "office-wear",
  freeride: "freeride",
  "vetements-lin": "vetements-lin",
  "made-in-usa": "made-in-usa",
  "made-in-portugal": "made-in-portugal",
  "made-in-spain": "made-in-spain",
  "made-in-canada": "made-in-canada",
  "made-in-australia": "made-in-australia",
  cycling: "cycling",
  climbing: "climbing",
  luxe: "luxe",
};

/**
 * Find a category by any slug (internal, FR, or EN).
 */
export const getTop10Category = (slug: string): Top10Category | undefined => {
  return top10Categories.find(
    (c) => c.slug === slug || c.slugFr === slug || c.slugEn === slug
  );
};

/**
 * Get the URL-facing slug for a category based on language.
 */
export const getCategoryUrlSlug = (cat: Top10Category, language: "fr" | "en"): string => {
  return language === "fr" ? cat.slugFr : cat.slugEn;
};
